import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Save, Send, Download, Trash2, Plus, Minus,
  Snowflake, Flame, Droplets, Wind, Euro, Tag, Percent,
  User, Phone, Mail, MapPin, Calendar, Check, X, Edit,
  FileText, Wifi, Star, RefreshCw, ShoppingCart, Clock,
  ChevronLeft, ChevronDown, Search, Zap, StickyNote, Sparkles
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { storeApi, formatApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { CATEGORY_STYLES } from '../components/store/storeConstants';
import { ProductCardAgent } from '../components/store/ProductCardAgent';
import { QuotePdfPanel } from '../components/store/QuotePdfPanel';
import { ConvertToSaleDialog } from '../components/store/ConvertToSaleDialog';
import { FinancingToggle } from '../components/store/FinancingToggle';

// Category Selection Card (top-level: evita ricreazione ad ogni render del parent)
const CategoryCard = ({ category, onSelect }) => {
  const Icon = category.icon;
  return (
    <div
      className={`relative rounded-2xl border-2 ${category.borderColor} ${category.bgColor} p-6 cursor-pointer transition-all duration-300 hover:shadow-xl hover:scale-[1.02] group`}
      onClick={() => onSelect(category)}
      data-testid={`category-card-${category.id}`}
    >
      {category.image && (
        <div className="absolute top-4 right-4 w-24 h-24 opacity-80 group-hover:opacity-100 transition-opacity">
          <img
            src={category.image}
            alt={category.label}
            className="w-full h-full object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}
      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center mb-4 shadow-lg`}>
        <Icon className="h-7 w-7 text-white" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-1">{category.label}</h3>
      <p className="text-sm text-gray-600">{category.description}</p>
    </div>
  );
};


// Main category groups for selection cards
const MAIN_CATEGORIES = [
  {
    id: 'CLIMATIZZATORE',
    label: 'Climatizzatori',
    description: 'Mono Split, Dual Split, Trial Split',
    icon: Snowflake,
    color: 'from-blue-500 to-cyan-400',
    bgColor: 'bg-gradient-to-br from-blue-50 to-cyan-50',
    borderColor: 'border-blue-200 hover:border-blue-400',
    image: 'https://eniplenitude.scene7.com/is/image/enigaseluce/928x328%20Card%202%20colonne_Multi3s:21-9?wid=400&hei=200',
    categories: ['CLIMATIZZATORE_MONO_SPLIT', 'CLIMATIZZATORE_DUAL_SPLIT', 'CLIMATIZZATORE_TRIAL_SPLIT', 'CLIMATIZZATORE_MONO', 'CLIMATIZZATORE_DUAL', 'CLIMATIZZATORE_TRIAL']
  },
  {
    id: 'CALDAIA',
    label: 'Caldaie',
    description: 'Condensazione ad alta efficienza',
    icon: Flame,
    color: 'from-orange-500 to-red-400',
    bgColor: 'bg-gradient-to-br from-orange-50 to-red-50',
    borderColor: 'border-orange-200 hover:border-orange-400',
    image: 'https://eniplenitude.scene7.com/is/image/enigaseluce/EGEL32X_Caldaia:21-9?wid=400&hei=200&fmt=png-alpha',
    categories: ['CALDAIA', 'CALDAIA_CONDENSAZIONE']
  },
  {
    id: 'SCALDACQUA',
    label: 'Scaldacqua',
    description: 'Elettrici, a gas e ibridi',
    icon: Droplets,
    color: 'from-cyan-500 to-teal-400',
    bgColor: 'bg-gradient-to-br from-cyan-50 to-teal-50',
    borderColor: 'border-cyan-200 hover:border-cyan-400',
    image: 'https://eniplenitude.scene7.com/is/image/enigaseluce/EGEL10X_Caldaia:21-9?wid=400&hei=200&fmt=png-alpha',
    categories: ['SCALDACQUA', 'SCALDACQUA_IBRIDO']
  },
  {
    id: 'POMPA_CALORE',
    label: 'Pompe di Calore',
    description: 'Sistemi ibridi e all-in-one',
    icon: Wind,
    color: 'from-green-500 to-emerald-400',
    bgColor: 'bg-gradient-to-br from-green-50 to-emerald-50',
    borderColor: 'border-green-200 hover:border-green-400',
    image: 'https://eniplenitude.scene7.com/is/image/enigaseluce/928x328%20Card%202%20colonne_PompaIbrida:21-9?wid=400&hei=200',
    categories: ['POMPA_DI_CALORE', 'POMPA_CALORE']
  }
];

// Product images: usa l'immagine reale del prodotto, con fallback per categoria
const getProductImage = (product) => {
  if (product.image_url) return product.image_url;
  const category = product.category;
  
  if (category?.includes('CLIMATIZZATORE')) {
    return 'https://eniplenitude.scene7.com/is/image/enigaseluce/928x328%20Card%202%20colonne_Multi3s:21-9?wid=300&hei=150';
  }
  if (category?.includes('CALDAIA')) {
    return 'https://eniplenitude.scene7.com/is/image/enigaseluce/EGEL32X_Caldaia:21-9?wid=300&hei=150&fmt=png-alpha';
  }
  if (category?.includes('SCALDACQUA')) {
    return 'https://eniplenitude.scene7.com/is/image/enigaseluce/EGEL10X_Caldaia:21-9?wid=300&hei=150&fmt=png-alpha';
  }
  if (category?.includes('POMPA')) {
    return 'https://eniplenitude.scene7.com/is/image/enigaseluce/928x328%20Card%202%20colonne_PompaIbrida:21-9?wid=300&hei=150';
  }
  return null;
};

const LINE_TYPE_LABELS = {
  'PRODUCT': 'Prodotto',
  'ACCESSORY': 'Accessorio',
  'SERVICE': 'Servizio',
  'PROMO_DISCOUNT': 'Sconto',
  'MANUAL_ADJUSTMENT': 'Rettifica'
};

const STATUS_CONFIG = {
  'draft': { label: 'Bozza', color: 'bg-gray-100 text-gray-700', icon: Edit },
  'sent': { label: 'Consegnato', color: 'bg-blue-100 text-blue-700', icon: Send },
  'accepted': { label: 'Accettato', color: 'bg-green-100 text-green-700', icon: Check },
  'rejected': { label: 'Rifiutato', color: 'bg-red-100 text-red-700', icon: X },
  'expired': { label: 'Scaduto', color: 'bg-orange-100 text-orange-700', icon: Clock },
  'converted': { label: 'Convertito', color: 'bg-purple-100 text-purple-700', icon: FileText }
};

// Stati selezionabili manualmente dall'agente (converted è riservato alla conversione in contratto)
const SELECTABLE_STATUSES = ['draft', 'sent', 'accepted', 'rejected', 'expired'];

export default function StoreQuoteDetailPage() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, isStoreManager } = useAuth();
  
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Add product flow
  const [showAddProductDialog, setShowAddProductDialog] = useState(false);
  const [selectedCategoryGroup, setSelectedCategoryGroup] = useState(null);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [onlyPromoFilter, setOnlyPromoFilter] = useState(false);
  
  // Edit customer dialog
  const [showEditCustomerDialog, setShowEditCustomerDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);

  // Convert to sale dialog (modal brandizzato)
  const [showConvertDialog, setShowConvertDialog] = useState(false);

  // Inline per-line edit dialog (note + sconto%)
  const [editingLineIndex, setEditingLineIndex] = useState(null);
  const [editingLineDraft, setEditingLineDraft] = useState({ discount_percent: 0, notes: '' });

  // Manual discount dialog
  const [showDiscountDialog, setShowDiscountDialog] = useState(false);
  const [discountDraft, setDiscountDraft] = useState({ amount: '', description: 'Sconto manuale' });

  // PDF versioning (varianti: full = completo 6 pagine, lite = sintetico 3 pagine)
  const [pdfVersions, setPdfVersions] = useState([]);
  const [pdfVariants, setPdfVariants] = useState({ full: {}, lite: {} });
  const [generatingVariant, setGeneratingVariant] = useState(null);

  // Load quote
  const loadQuote = useCallback(async () => {
    setLoading(true);
    try {
      const response = await storeApi.getQuote(quoteId);
      setQuote(response.data);
    } catch (error) {
      toast.error(formatApiError(error));
      navigate('/store');
    } finally {
      setLoading(false);
    }
  }, [quoteId, navigate]);

  const loadPdfVersions = useCallback(async () => {
    try {
      const response = await storeApi.getQuotePdfVersions(quoteId);
      setPdfVersions(response.data.versions || []);
      setPdfVariants(response.data.variants || { full: {}, lite: {} });
    } catch (error) {
      console.error('Error loading PDF versions:', error);
    }
  }, [quoteId]);

  useEffect(() => {
    loadQuote();
  }, [loadQuote]);

  useEffect(() => {
    loadPdfVersions();
  }, [loadPdfVersions, quote?.updated_at]);

  // Load products for selected category (e per ricerca globale)
  // categoryGroup può essere null = nessun filtro categoria (mostra tutto attivo)
  const loadProductsByCategory = async (categoryGroup, search = '', onlyPromo = false) => {
    setProductsLoading(true);
    try {
      const params = { is_active: true, limit: 100 };
      if (categoryGroup?.categories) {
        params.categories = categoryGroup.categories.join(',');
      }
      if (search) params.search = search;
      if (onlyPromo) params.has_promo = true;
      const response = await storeApi.getProducts(params);
      setProducts(response.data.items || []);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setProductsLoading(false);
    }
  };

  // Add product to quote
  const handleAddProduct = async () => {
    if (!selectedProduct) return;
    
    setSaving(true);
    try {
      await storeApi.addProductToQuote(quoteId, {
        product_id: selectedProduct.id,
        quantity: 1,
        selected_thermostat: false,
        selected_promos: []
      });
      
      toast.success('Prodotto aggiunto al preventivo');
      setShowAddProductDialog(false);
      setSelectedProduct(null);
      setSelectedCategoryGroup(null);
      loadQuote();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  // Remove line from quote
  const handleRemoveLine = async (lineIndex) => {
    if (!window.confirm('Rimuovere questa riga dal preventivo?')) return;
    
    setSaving(true);
    try {
      await storeApi.removeLineFromQuote(quoteId, lineIndex);
      toast.success('Riga rimossa');
      loadQuote();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  // Update quote status
  const handleUpdateStatus = async (newStatus) => {
    setSaving(true);
    try {
      await storeApi.updateQuoteStatus(quoteId, newStatus);
      toast.success(`Stato aggiornato: ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      loadQuote();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  // Convert quote to sale (status must be 'accepted')
  // Apre il modal brandizzato (sostituisce window.confirm + window.prompt)
  const handleOpenConvertDialog = () => setShowConvertDialog(true);

  const handleConfirmConvert = async ({ contract_id, notes }) => {
    try {
      await storeApi.convertQuoteToSale(quoteId, {
        contract_id: contract_id || null,
        notes: notes || null,
      });
      toast.success('Preventivo convertito in vendita');
      setShowConvertDialog(false);
      loadQuote();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  // Aggiorna quantità di una riga (debounced lato UI tramite optimistic update)
  const handleUpdateLineQty = async (lineIndex, newQty) => {
    if (newQty < 1) return;
    // Optimistic update
    setQuote((q) => {
      if (!q) return q;
      const lines = q.lines ? [...q.lines] : [];
      if (lines[lineIndex]) {
        const ln = { ...lines[lineIndex], quantity: newQty };
        const disc = (ln.discount_percent || 0) / 100;
        ln.line_total = Math.round((ln.unit_price || 0) * newQty * (1 - disc) * 100) / 100;
        lines[lineIndex] = ln;
      }
      return { ...q, lines };
    });
    try {
      const res = await storeApi.updateQuoteLine(quoteId, lineIndex, { quantity: newQty });
      setQuote(res.data);
    } catch (error) {
      toast.error(formatApiError(error));
      loadQuote(); // rollback
    }
  };

  // Apre dialog editing riga (sconto% + note)
  const openLineEditor = (lineIndex) => {
    const line = quote?.lines?.[lineIndex];
    if (!line) return;
    setEditingLineIndex(lineIndex);
    setEditingLineDraft({
      discount_percent: line.discount_percent || 0,
      notes: line.notes || '',
    });
  };

  const saveLineEditor = async () => {
    if (editingLineIndex === null) return;
    try {
      const res = await storeApi.updateQuoteLine(quoteId, editingLineIndex, {
        discount_percent: Number(editingLineDraft.discount_percent) || 0,
        notes: editingLineDraft.notes,
      });
      setQuote(res.data);
      toast.success('Riga aggiornata');
      setEditingLineIndex(null);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  // Manual discount
  const handleAddManualDiscount = async () => {
    const amount = parseFloat(String(discountDraft.amount).replace(',', '.'));
    if (!amount || amount <= 0) {
      toast.error("Inserisci un importo valido maggiore di 0");
      return;
    }
    setSaving(true);
    try {
      const res = await storeApi.addManualDiscount(quoteId, {
        amount,
        description: discountDraft.description?.trim() || 'Sconto manuale',
      });
      setQuote(res.data);
      toast.success(`Sconto di € ${amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })} applicato`);
      setShowDiscountDialog(false);
      setDiscountDraft({ amount: '', description: 'Sconto manuale' });
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  // Save customer changes
  const handleSaveCustomer = async () => {
    setSaving(true);
    try {
      await storeApi.updateQuote(quoteId, { customer: editingCustomer });
      toast.success('Dati cliente aggiornati');
      setShowEditCustomerDialog(false);
      loadQuote();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  // Download PDF completo dall'header (scarica ultima versione salvata; se assente o non aggiornata, genera prima)
  const handleDownloadPdf = async () => {
    const fullInfo = pdfVariants.full || {};
    if (fullInfo.latest_version && !fullInfo.outdated) {
      const latest = pdfVersions.find((v) => (v.variant || 'full') === 'full');
      if (latest) {
        try {
          await storeApi.downloadQuotePdfVersion(quoteId, latest.version, latest.filename, 'full');
          toast.success(`PDF ${latest.filename} scaricato`);
        } catch (error) {
          toast.error(formatApiError(error));
        }
        return;
      }
    }
    await handleGeneratePdf('full');
  };

  // Genera nuova versione PDF (completo o lite), salvala nel CRM e scaricala
  const handleGeneratePdf = async (variant = 'full') => {
    setGeneratingVariant(variant);
    try {
      const response = await storeApi.generateQuotePdf(quoteId, variant);
      const { version, filename } = response.data;
      await storeApi.downloadQuotePdfVersion(quoteId, version, filename, variant);
      toast.success(`PDF generato e salvato: ${filename}`);
      loadPdfVersions();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setGeneratingVariant(null);
    }
  };

  const handleDownloadPdfVersion = async (v) => {
    try {
      await storeApi.downloadQuotePdfVersion(quoteId, v.version, v.filename, v.variant || 'full');
      toast.success(`PDF ${v.filename} scaricato`);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  // Open add product dialog
  const openAddProductDialog = () => {
    setShowAddProductDialog(true);
    setSelectedCategoryGroup(null);
    setSelectedProduct(null);
    setProductSearch('');
    setOnlyPromoFilter(false);
    // Carica subito TUTTI i prodotti attivi: l'agente vede subito il catalogo (richiesta utente)
    loadProductsByCategory(null, '', false);
  };

  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-64 bg-gray-200 rounded" />
          <div className="h-4 w-48 bg-gray-200 rounded" />
          <div className="h-64 bg-gray-200 rounded-lg" />
        </div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="p-6 max-w-6xl mx-auto text-center">
        <p className="text-lg">Preventivo non trovato</p>
        <Button onClick={() => navigate('/store')} className="mt-4">
          Torna al catalogo
        </Button>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[quote.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusConfig.icon;
  // Responsabile Store: sola lettura sui preventivi altrui
  const isOwner = quote.agent_id === user?.id || quote.created_by === user?.id;
  const canModify = isAdmin || !isStoreManager || isOwner;
  const isDraft = quote.status === 'draft' && canModify;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/store')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{quote.quote_number}</h1>
              <Badge className={statusConfig.color}>
                <StatusIcon className="h-3 w-3 mr-1" />
                {statusConfig.label}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Creato il {new Date(quote.created_at).toLocaleDateString('it-IT')} • 
              Valido fino al {new Date(quote.valid_until).toLocaleDateString('it-IT')}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {canModify && quote.status === 'accepted' && !quote.converted_to_sale && (
            <Button
              variant="default"
              className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={handleOpenConvertDialog}
              disabled={saving}
              data-testid="convert-to-sale-btn"
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              Converti in Vendita
            </Button>
          )}
          {canModify && quote.status !== 'converted' && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={saving} data-testid="change-status-btn">
                  <StatusIcon className="h-4 w-4 mr-2" />
                  Cambia stato
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Stato preventivo</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {SELECTABLE_STATUSES.map((s) => {
                  const cfg = STATUS_CONFIG[s];
                  const Icon = cfg.icon;
                  const isCurrent = quote.status === s;
                  return (
                    <DropdownMenuItem
                      key={s}
                      disabled={isCurrent}
                      onClick={() => handleUpdateStatus(s)}
                      data-testid={`status-option-${s}`}
                    >
                      <Icon className="h-4 w-4 mr-2" />
                      <span className="flex-1">{cfg.label}</span>
                      {isCurrent && <Check className="h-4 w-4 text-green-600" />}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button onClick={handleDownloadPdf} disabled={saving || generatingVariant !== null} data-testid="header-download-pdf-btn">
            <Download className="h-4 w-4 mr-2" />
            Scarica PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - 2 cols */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Products/Lines */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div>
                <CardTitle className="text-lg">Prodotti e Servizi</CardTitle>
                <CardDescription>{quote.lines?.length || 0} righe</CardDescription>
              </div>
              {isDraft && quote.lines?.length > 0 && (
                <div className="flex items-center gap-2">
                  {isAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDiscountDialog(true)}
                      data-testid="add-discount-btn"
                      title="Aggiungi uno sconto manuale al preventivo (solo admin)"
                    >
                      <Percent className="h-4 w-4 mr-2 text-green-600" />
                      Aggiungi Sconto
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={openAddProductDialog}
                    data-testid="add-product-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Aggiungi Prodotto
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {quote.lines?.length === 0 ? (
                /* Empty state - Show category cards */
                <div className="py-4">
                  <p className="text-center text-muted-foreground mb-6">
                    Seleziona una tipologia di prodotto per iniziare
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {MAIN_CATEGORIES.map(category => (
                      <CategoryCard 
                        key={category.id}
                        category={category}
                        onSelect={() => {
                          setSelectedCategoryGroup(category);
                          setSelectedProduct(null);
                          setProductSearch('');
                          setOnlyPromoFilter(false);
                          setShowAddProductDialog(true);
                          loadProductsByCategory(category, '', false);
                        }}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {quote.lines.map((line, index) => {
                    const isDiscount = line.line_type === 'PROMO_DISCOUNT' || line.line_type === 'MANUAL_ADJUSTMENT';
                    const isService = line.line_type === 'SERVICE' || line.line_type === 'ACCESSORY';
                    const isProduct = line.line_type === 'PRODUCT';
                    const style = CATEGORY_STYLES[line.product_snapshot?.category] || {};
                    const CategoryIcon = style.icon || Tag;
                    const productImage = line.product_snapshot ? getProductImage(line.product_snapshot) : null;
                    const hasDiscount = (line.discount_percent || 0) > 0;
                    const grossPrice = (line.unit_price || 0) * (line.quantity || 1);
                    
                    // Chiave stabile: usa product_code se disponibile, altrimenti combo descrizione+tipo+index come fallback
                    const stableKey =
                      line.product_snapshot?.product_code
                        ? `prod-${line.product_snapshot.product_code}-${index}`
                        : `${line.line_type}-${(line.description || 'l').slice(0, 20)}-${index}`;

                    return (
                      <div 
                        key={stableKey}
                        className={`flex items-center gap-4 p-4 rounded-xl border ${
                          isDiscount ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
                        }`}
                        data-testid={`quote-line-${index}`}
                      >
                        {/* Product Image or Icon */}
                        {productImage && !isDiscount ? (
                          <div className="w-20 h-20 rounded-lg bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                            <img 
                              src={productImage} 
                              alt={line.description}
                              className="w-full h-full object-contain"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </div>
                        ) : (
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            isDiscount ? 'bg-green-100' : style.color || 'bg-blue-50'
                          }`}>
                            {isDiscount ? (
                              <Percent className="h-6 w-6 text-green-600" />
                            ) : (
                              <CategoryIcon className="h-6 w-6" />
                            )}
                          </div>
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">{line.description}</p>
                          <p className="text-sm text-muted-foreground">
                            {LINE_TYPE_LABELS[line.line_type] || line.line_type}
                            {!isDiscount && (
                              <>
                                {' • '}
                                {line.quantity} {line.unit}
                              </>
                            )}
                            {line.iva_rate > 0 && ` • IVA ${line.iva_rate}% incl.`}
                            {hasDiscount && (
                              <>
                                {' • '}
                                <span className="text-amber-600 font-medium">sconto -{line.discount_percent}%</span>
                              </>
                            )}
                          </p>
                          {line.notes && (
                            <p className="text-xs text-slate-500 italic mt-1 flex items-center gap-1">
                              <StickyNote className="h-3 w-3" /> {line.notes}
                            </p>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-3">
                          {/* +/- inline per quantità prodotti/accessori in bozza */}
                          {isDraft && isProduct && (
                            <div
                              className="flex items-center border-2 border-slate-200 rounded-lg overflow-hidden bg-white"
                              data-testid={`qty-control-${index}`}
                            >
                              <button
                                type="button"
                                className="w-8 h-8 flex items-center justify-center text-[#0B2A4A] hover:bg-slate-100 disabled:opacity-30"
                                onClick={() => handleUpdateLineQty(index, (line.quantity || 1) - 1)}
                                disabled={saving || (line.quantity || 1) <= 1}
                                data-testid={`qty-decrement-${index}`}
                                aria-label="Diminuisci quantità"
                              >
                                <Minus className="h-3.5 w-3.5" />
                              </button>
                              <span className="px-3 font-bold text-sm min-w-[2rem] text-center select-none" data-testid={`qty-value-${index}`}>
                                {line.quantity || 1}
                              </span>
                              <button
                                type="button"
                                className="w-8 h-8 flex items-center justify-center text-[#0B2A4A] hover:bg-slate-100 disabled:opacity-30"
                                onClick={() => handleUpdateLineQty(index, (line.quantity || 1) + 1)}
                                disabled={saving}
                                data-testid={`qty-increment-${index}`}
                                aria-label="Aumenta quantità"
                              >
                                <Plus className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}

                          <div className="text-right">
                            <p className={`text-lg font-bold ${isDiscount ? 'text-green-600' : ''}`}>
                              {isDiscount ? '-' : ''}€ {Math.abs(line.line_total || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                            </p>
                            {!isDiscount && (line.unit_price || 0) > 0 && (
                              <p className="text-xs text-muted-foreground">
                                € {line.unit_price?.toLocaleString('it-IT', { minimumFractionDigits: 2 })} × {line.quantity || 1}
                                {hasDiscount && (
                                  <> · <span className="line-through opacity-60">€ {grossPrice.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span></>
                                )}
                              </p>
                            )}
                          </div>
                          
                          {isDraft && (
                            <div className="flex items-center gap-1">
                              {/* Modifica sconto/note solo su prodotti */}
                              {isProduct && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-slate-500 hover:text-[#00BCE7] hover:bg-cyan-50"
                                  onClick={() => openLineEditor(index)}
                                  data-testid={`edit-line-${index}`}
                                  title="Modifica sconto e note"
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleRemoveLine(index)}
                                data-testid={`remove-line-${index}`}
                                title="Rimuovi riga"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Note</CardTitle>
            </CardHeader>
            <CardContent>
              {isDraft ? (
                <Textarea 
                  placeholder="Aggiungi note per il cliente..."
                  value={quote.notes || ''}
                  onChange={async (e) => {
                    const newNotes = e.target.value;
                    setQuote(q => ({ ...q, notes: newNotes }));
                    clearTimeout(window.noteSaveTimeout);
                    window.noteSaveTimeout = setTimeout(async () => {
                      try {
                        await storeApi.updateQuote(quoteId, { notes: newNotes });
                      } catch (err) {
                        console.error('Error saving notes:', err);
                      }
                    }, 1000);
                  }}
                  rows={3}
                />
              ) : (
                <p className="text-muted-foreground">{quote.notes || 'Nessuna nota'}</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - 1 col */}
        <div className="space-y-6">
          
          {/* Totals */}
          <Card className="bg-slate-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Riepilogo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Prodotti</span>
                <span>€ {quote.total_products?.toLocaleString('it-IT', { minimumFractionDigits: 2 }) || '0,00'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Servizi</span>
                <span>€ {quote.total_services?.toLocaleString('it-IT', { minimumFractionDigits: 2 }) || '0,00'}</span>
              </div>
              {quote.total_discounts > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Sconti</span>
                  <span>- € {quote.total_discounts?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Imponibile (scorporato)</span>
                <span>€ {quote.total_net?.toLocaleString('it-IT', { minimumFractionDigits: 2 }) || '0,00'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">IVA</span>
                <span>€ {quote.iva_amount?.toLocaleString('it-IT', { minimumFractionDigits: 2 }) || '0,00'}</span>
              </div>
              <Separator />
              <div className="flex justify-between items-baseline">
                <span className="text-lg font-bold">Totale</span>
                <div className="text-right">
                  <div className="text-lg font-bold text-blue-600" data-testid="quote-total-gross">
                    € {quote.total_gross?.toLocaleString('it-IT', { minimumFractionDigits: 2 }) || '0,00'}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">IVA inclusa</div>
                </div>
              </div>
              
              {(() => {
                // Mostra il mini-riepilogo solo se selected_financing è ancora supportato dai prodotti in carrello
                if (!quote.selected_financing) return null;
                const supported = new Set();
                (quote.lines || []).forEach((l) => {
                  const s = l.product_snapshot || {};
                  if (s.tasso_zero_available) supported.add(`TASSO_ZERO_${s.financing_months || 24}`);
                  if (s.fido_in_bolletta) supported.add(`FIDO_BOLLETTA_${s.fido_months || 48}`);
                });
                if (!supported.has(quote.selected_financing)) return null;
                return (
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg" data-testid="financing-summary-mini">
                    <div className="flex items-center gap-2 text-amber-700">
                      <Euro className="h-4 w-4" />
                      <span className="font-medium">Finanziamento attivo</span>
                    </div>
                    <p className="text-sm text-amber-600 mt-1">
                      {quote.selected_financing === 'TASSO_ZERO_24' && 'Tasso Zero in 24 rate'}
                      {quote.selected_financing === 'TASSO_ZERO_48' && 'Tasso Zero in 48 rate'}
                      {quote.selected_financing === 'FIDO_BOLLETTA_48' && 'Fido in bolletta · 48 rate'}
                      {quote.selected_financing === 'FINANZIAMENTO' && 'Finanziamento'}
                    </p>
                    {(() => {
                      const m = quote.selected_financing?.match(/_(\d+)$/);
                      const months = m ? parseInt(m[1], 10) : null;
                      if (!months || !quote.total_gross) return null;
                      return (
                        <p className="text-lg font-bold text-amber-700 mt-1">
                          € {(quote.total_gross / months).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mese
                        </p>
                      );
                    })()}
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          {/* Finanziamento toggle (solo in bozza, agente attiva manualmente) */}
          {isDraft && (
            <FinancingToggle
              quote={quote}
              onChange={(updated) => setQuote(updated)}
              disabled={saving}
            />
          )}

          {/* Documenti PDF */}
          <QuotePdfPanel
            versions={pdfVersions}
            variants={pdfVariants}
            generatingVariant={generatingVariant}
            onGenerate={handleGeneratePdf}
            onDownloadVersion={handleDownloadPdfVersion}
          />

          {/* Customer */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">Cliente</CardTitle>
              {isDraft && (
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => {
                    setEditingCustomer({ ...quote.customer });
                    setShowEditCustomerDialog(true);
                  }}
                >
                  <Edit className="h-4 w-4" />
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">
                  {quote.customer?.nome} {quote.customer?.cognome}
                </span>
              </div>
              
              {quote.customer?.telefono && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <a href={`tel:${quote.customer.telefono}`} className="text-blue-600 hover:underline">
                    {quote.customer.telefono}
                  </a>
                </div>
              )}
              
              {quote.customer?.email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <a href={`mailto:${quote.customer.email}`} className="text-blue-600 hover:underline">
                    {quote.customer.email}
                  </a>
                </div>
              )}
              
              {quote.customer?.indirizzo && (
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <span>
                    {quote.customer.indirizzo}
                    {quote.customer.citta && `, ${quote.customer.citta}`}
                    {quote.customer.provincia && ` (${quote.customer.provincia})`}
                  </span>
                </div>
              )}
              
              <div className="flex flex-wrap gap-2 mt-2">
                {quote.customer?.is_existing_customer && (
                  <Badge variant="secondary" className="text-xs">
                    <Check className="h-3 w-3 mr-1" />
                    Cliente esistente
                  </Badge>
                )}
                {quote.customer?.wants_to_become_customer && (
                  <Badge variant="outline" className="text-xs">
                    Nuovo cliente
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Agent Info */}
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-muted-foreground">
                <p>Agente: <span className="text-foreground">{quote.agent_name || '-'}</span></p>
                <p className="mt-1">Store: <span className="text-foreground">{quote.store_id || '-'}</span></p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add Product Dialog - vista unica con ricerca globale + filtri visibili */}
      <Dialog open={showAddProductDialog} onOpenChange={setShowAddProductDialog}>
        <DialogContent className="max-w-5xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="add-product-dialog">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="flex items-center gap-2 text-[#0B2A4A]">
              <ShoppingCart className="h-5 w-5 text-[#00BCE7]" />
              Aggiungi prodotto al preventivo
            </DialogTitle>
            <DialogDescription>
              Cerca per modello/marca o filtra per categoria. Click sulla card per selezionare, poi conferma in basso.
            </DialogDescription>
          </DialogHeader>

          {/* Toolbar filtri sempre visibile */}
          <div className="border-b bg-slate-50/60 px-1 py-3 space-y-3">
            {/* Search globale */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#00BCE7]" />
              <Input
                placeholder="Cerca prodotto: marca, modello, codice... (es. haier 9000, alteas, velis)"
                value={productSearch}
                onChange={(e) => {
                  const v = e.target.value;
                  setProductSearch(v);
                  loadProductsByCategory(selectedCategoryGroup, v, onlyPromoFilter);
                }}
                className="pl-10 h-11 text-base border-2 focus:border-[#00BCE7]"
                data-testid="global-product-search"
                autoFocus
              />
            </div>

            {/* Chip filtri categoria + promo */}
            <div className="flex flex-wrap gap-2 items-center" data-testid="filter-chips">
              <button
                type="button"
                onClick={() => {
                  setSelectedCategoryGroup(null);
                  loadProductsByCategory(null, productSearch, onlyPromoFilter);
                }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-all ${
                  selectedCategoryGroup === null
                    ? 'bg-[#0B2A4A] text-white border-[#0B2A4A]'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                }`}
                data-testid="filter-cat-all"
              >
                Tutte le categorie
              </button>
              {MAIN_CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                const active = selectedCategoryGroup?.id === cat.id;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => {
                      setSelectedCategoryGroup(cat);
                      loadProductsByCategory(cat, productSearch, onlyPromoFilter);
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-all ${
                      active
                        ? 'bg-[#00BCE7] text-white border-[#00BCE7] shadow-sm'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-[#00BCE7]'
                    }`}
                    data-testid={`filter-cat-${cat.id}`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {cat.label}
                  </button>
                );
              })}
              <div className="h-5 w-px bg-slate-300 mx-1" />
              <button
                type="button"
                onClick={() => {
                  const next = !onlyPromoFilter;
                  setOnlyPromoFilter(next);
                  loadProductsByCategory(selectedCategoryGroup, productSearch, next);
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-all ${
                  onlyPromoFilter
                    ? 'bg-amber-400 text-amber-950 border-amber-500 shadow-sm'
                    : 'bg-white text-amber-700 border-amber-200 hover:border-amber-400'
                }`}
                data-testid="filter-only-promo"
              >
                <Sparkles className="h-3.5 w-3.5" />
                Solo prodotti in promo
              </button>
            </div>
          </div>

          {/* Lista prodotti */}
          <div className="flex-1 overflow-y-auto p-3" data-testid="products-grid-container">
            {productsLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={`skeleton-prod-${i}`} className="h-72 bg-gray-100 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20 text-muted-foreground" data-testid="no-products-msg">
                <Snowflake className="h-14 w-14 mx-auto mb-4 opacity-30" />
                <p className="text-base font-medium">Nessun prodotto trovato</p>
                <p className="text-sm mt-1">Prova a modificare la ricerca o rimuovere i filtri.</p>
              </div>
            ) : (
              <>
                <div className="text-xs text-slate-500 mb-2 px-1">
                  {products.length} prodotti trovati
                  {selectedCategoryGroup ? ` in "${selectedCategoryGroup.label}"` : ''}
                  {productSearch ? ` per "${productSearch}"` : ''}
                  {onlyPromoFilter ? ' · solo promo attive' : ''}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {products.map((product) => (
                    <ProductCardAgent
                      key={product.id}
                      product={product}
                      isSelected={selectedProduct?.id === product.id}
                      onSelect={() => setSelectedProduct(product)}
                      showButton={false}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          <DialogFooter className="border-t pt-4 px-1 gap-2 items-center">
            <div className="flex-1 text-sm">
              {selectedProduct ? (
                <span className="text-[#0B2A4A]">
                  Selezionato: <strong>{selectedProduct.brand} {selectedProduct.model}</strong>
                </span>
              ) : (
                <span className="text-slate-400">Seleziona un prodotto per continuare</span>
              )}
            </div>
            <Button variant="outline" onClick={() => setShowAddProductDialog(false)} data-testid="cancel-add-product-btn">
              Annulla
            </Button>
            <Button
              onClick={handleAddProduct}
              disabled={!selectedProduct || saving}
              className="min-w-[200px] bg-[#0B2A4A] hover:bg-[#0a253f] text-white"
              data-testid="confirm-add-product-btn"
            >
              {saving ? 'Aggiunta...' : (<><Plus className="h-4 w-4 mr-2" /> Aggiungi al Preventivo</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to sale Dialog (brandizzato) */}
      <ConvertToSaleDialog
        open={showConvertDialog}
        onClose={() => setShowConvertDialog(false)}
        quote={quote}
        onConfirm={handleConfirmConvert}
      />

      {/* Manual discount Dialog */}
      <Dialog open={showDiscountDialog} onOpenChange={setShowDiscountDialog}>
        <DialogContent className="max-w-sm" data-testid="manual-discount-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Percent className="h-5 w-5 text-green-600" />
              Aggiungi sconto manuale
            </DialogTitle>
            <DialogDescription>
              Verrà aggiunta una riga di sconto sul totale del preventivo (IVA inclusa).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wide text-[#0B2A4A] font-semibold">
                Importo sconto (€)
              </Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                inputMode="decimal"
                value={discountDraft.amount}
                onChange={(e) => setDiscountDraft((d) => ({ ...d, amount: e.target.value }))}
                placeholder="Es: 300"
                className="mt-2 text-lg font-semibold"
                autoFocus
                data-testid="manual-discount-amount"
              />
              <p className="text-xs text-slate-500 mt-1">Importo positivo. Verrà sottratto dal totale.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-[#0B2A4A] font-semibold">
                Descrizione
              </Label>
              <Input
                value={discountDraft.description}
                onChange={(e) => setDiscountDraft((d) => ({ ...d, description: e.target.value }))}
                placeholder="Es: Sconto cliente fidelizzato"
                className="mt-2"
                data-testid="manual-discount-description"
              />
              <p className="text-xs text-slate-500 mt-1">Etichetta visibile nel PDF cliente.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiscountDialog(false)}>Annulla</Button>
            <Button
              onClick={handleAddManualDiscount}
              disabled={saving || !discountDraft.amount}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="confirm-manual-discount-btn"
            >
              <Percent className="h-4 w-4 mr-2" /> Applica sconto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editor riga (sconto% + note) */}
      <Dialog open={editingLineIndex !== null} onOpenChange={(o) => !o && setEditingLineIndex(null)}>
        <DialogContent className="max-w-sm" data-testid="line-editor-dialog">
          <DialogHeader>
            <DialogTitle>Modifica riga</DialogTitle>
            <DialogDescription>
              Imposta uno sconto in percentuale e/o aggiungi una nota interna per questa riga.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wide text-[#0B2A4A] font-semibold">
                Sconto sulla riga (%)
              </Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={editingLineDraft.discount_percent}
                onChange={(e) => setEditingLineDraft((d) => ({ ...d, discount_percent: e.target.value }))}
                placeholder="0"
                className="mt-2"
                data-testid="line-discount-input"
              />
              <p className="text-xs text-slate-500 mt-1">Es: 5 = 5% di sconto sul totale riga</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-[#0B2A4A] font-semibold">
                Note riga
              </Label>
              <Textarea
                rows={3}
                value={editingLineDraft.notes}
                onChange={(e) => setEditingLineDraft((d) => ({ ...d, notes: e.target.value }))}
                placeholder="Es: consegna piano 3, installazione su parete in cartongesso..."
                className="mt-2 resize-none"
                data-testid="line-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingLineIndex(null)}>Annulla</Button>
            <Button onClick={saveLineEditor} className="bg-[#00BCE7] hover:bg-[#00a8d1] text-white" data-testid="save-line-edit-btn">
              <Check className="h-4 w-4 mr-2" /> Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={showEditCustomerDialog} onOpenChange={setShowEditCustomerDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifica Dati Cliente</DialogTitle>
          </DialogHeader>
          
          {editingCustomer && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nome</Label>
                  <Input 
                    value={editingCustomer.nome || ''}
                    onChange={(e) => setEditingCustomer(c => ({ ...c, nome: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Cognome</Label>
                  <Input 
                    value={editingCustomer.cognome || ''}
                    onChange={(e) => setEditingCustomer(c => ({ ...c, cognome: e.target.value }))}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Telefono</Label>
                  <Input 
                    value={editingCustomer.telefono || ''}
                    onChange={(e) => setEditingCustomer(c => ({ ...c, telefono: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input 
                    type="email"
                    value={editingCustomer.email || ''}
                    onChange={(e) => setEditingCustomer(c => ({ ...c, email: e.target.value }))}
                  />
                </div>
              </div>
              
              <div>
                <Label>Indirizzo</Label>
                <Input 
                  value={editingCustomer.indirizzo || ''}
                  onChange={(e) => setEditingCustomer(c => ({ ...c, indirizzo: e.target.value }))}
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <Label>Città</Label>
                  <Input 
                    value={editingCustomer.citta || ''}
                    onChange={(e) => setEditingCustomer(c => ({ ...c, citta: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Prov.</Label>
                  <Input 
                    value={editingCustomer.provincia || ''}
                    onChange={(e) => setEditingCustomer(c => ({ ...c, provincia: e.target.value.toUpperCase().slice(0, 2) }))}
                    maxLength={2}
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={editingCustomer.is_existing_customer || false}
                    onChange={(e) => setEditingCustomer(c => ({ ...c, is_existing_customer: e.target.checked }))}
                    className="rounded"
                  />
                  <span className="text-sm">Già cliente luce/gas</span>
                </label>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditCustomerDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleSaveCustomer} disabled={saving}>
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
